import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Loader2, Gift, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const CASE_CONFIGS = {
  bronze: {
    name: 'Bronze',
    price: 50,
    gradient: 'from-amber-700 to-amber-900',
    borderColor: 'border-amber-600/50',
    items: [
      { name: 'Common Item', value: 10, color: '#a1a1aa' },
      { name: 'Uncommon Item', value: 50, color: '#22c55e' },
      { name: 'Rare Item', value: 150, color: '#3b82f6' },
      { name: 'Epic Item', value: 500, color: '#a855f7' }
    ]
  },
  silver: {
    name: 'Silver',
    price: 150,
    gradient: 'from-slate-400 to-slate-600',
    borderColor: 'border-slate-400/50',
    items: [
      { name: 'Uncommon Item', value: 50, color: '#22c55e' },
      { name: 'Rare Item', value: 200, color: '#3b82f6' },
      { name: 'Epic Item', value: 600, color: '#a855f7' },
      { name: 'Legendary Item', value: 1500, color: '#f59e0b' }
    ]
  },
  gold: {
    name: 'Gold',
    price: 500,
    gradient: 'from-yellow-400 to-yellow-600',
    borderColor: 'border-yellow-400/50',
    items: [
      { name: 'Rare Item', value: 300, color: '#3b82f6' },
      { name: 'Epic Item', value: 800, color: '#a855f7' },
      { name: 'Legendary Item', value: 2000, color: '#f59e0b' },
      { name: 'Mythic Item', value: 5000, color: '#ef4444' }
    ]
  },
  platinum: {
    name: 'Platinum',
    price: 1500,
    gradient: 'from-cyan-300 to-cyan-500',
    borderColor: 'border-cyan-300/50',
    items: [
      { name: 'Epic Item', value: 1000, color: '#a855f7' },
      { name: 'Legendary Item', value: 3000, color: '#f59e0b' },
      { name: 'Mythic Item', value: 7500, color: '#ef4444' },
      { name: 'Divine Item', value: 20000, color: '#ec4899' }
    ]
  }
};

export default function Cases() {
  const { api, updateBalance, user } = useAuth();
  const [selectedCase, setSelectedCase] = useState('bronze');
  const [loading, setLoading] = useState(false);
  const [opening, setOpening] = useState(false);
  const [result, setResult] = useState(null);
  const [scrollItems, setScrollItems] = useState([]);

  // Set page title
  useEffect(() => {
    document.title = 'RBX.Casino | Cases';
  }, []);

  const generateScrollItems = (caseType) => {
    const items = CASE_CONFIGS[caseType].items;
    const scrollArray = [];
    // Generate 50 random items for the scroll animation
    for (let i = 0; i < 50; i++) {
      const randomItem = items[Math.floor(Math.random() * items.length)];
      scrollArray.push({ ...randomItem, id: i });
    }
    return scrollArray;
  };

  const handleOpen = async () => {
    if (!user) {
      toast.error('Please login to play');
      return;
    }

    const caseConfig = CASE_CONFIGS[selectedCase];
    
    if (user.site_balance < caseConfig.price) {
      toast.error('Insufficient balance');
      return;
    }

    setLoading(true);
    setResult(null);

    try {
      const response = await api.post('/games/cases/open', { 
        amount: caseConfig.price, 
        case_type: selectedCase 
      });
      
      // Generate scroll items and place the winning item at position 44 (near the end)
      const items = generateScrollItems(selectedCase);
      items[44] = { ...response.data.won_item, id: 44 };
      setScrollItems(items);
      
      setOpening(true);

      // Wait for animation
      await new Promise(resolve => setTimeout(resolve, 3500));

      setResult(response.data);
      updateBalance(response.data.new_balance);
      
      const profit = response.data.won_item.value - caseConfig.price;
      if (profit > 0) {
        toast.success(`Won ${response.data.won_item.name}! +R$ ${profit}`);
      } else {
        toast.info(`Got ${response.data.won_item.name} worth R$ ${response.data.won_item.value}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to open case');
    } finally {
      setLoading(false);
      setOpening(false);
    }
  };

  const caseConfig = CASE_CONFIGS[selectedCase];

  return (
    <div className="max-w-5xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-2">
          <span className="text-cyan-400">Cases</span>
        </h1>
        <p className="text-slate-400">Open cases, win big - Random.org powered</p>
      </div>

      {/* Case Opening Animation */}
      <div className="glass rounded-2xl p-6 mb-8 overflow-hidden">
        <div className="relative h-32">
          {/* Center indicator */}
          <div className="absolute left-1/2 top-0 bottom-0 w-1 bg-yellow-400 z-20 transform -translate-x-1/2">
            <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-yellow-400 rotate-45" />
            <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-yellow-400 rotate-45" />
          </div>

          {/* Scrolling items */}
          <div className="relative h-full overflow-hidden">
            <motion.div
              className="flex items-center gap-2 absolute"
              initial={{ x: 0 }}
              animate={opening ? { x: -(44 * 108) + (window.innerWidth / 2 - 50) } : { x: 0 }}
              transition={{ duration: 3, ease: [0.17, 0.67, 0.12, 0.99] }}
            >
              {(opening ? scrollItems : caseConfig.items.concat(caseConfig.items).concat(caseConfig.items)).map((item, i) => (
                <div
                  key={i}
                  className="flex-shrink-0 w-24 h-24 rounded-xl flex flex-col items-center justify-center p-2"
                  style={{ 
                    backgroundColor: `${item.color}20`,
                    border: `2px solid ${item.color}50`
                  }}
                >
                  <Sparkles size={24} style={{ color: item.color }} />
                  <p className="text-xs text-center text-slate-300 mt-1 truncate w-full">{item.name}</p>
                  <p className="text-xs font-mono" style={{ color: item.color }}>R$ {item.value}</p>
                </div>
              ))}
            </motion.div>
          </div>
        </div>

        {/* Result Display */}
        <AnimatePresence>
          {result && !opening && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-6 text-center"
            >
              <div 
                className="inline-block p-6 rounded-xl"
                style={{ 
                  backgroundColor: `${result.won_item.color}20`,
                  border: `2px solid ${result.won_item.color}`
                }}
              >
                <Sparkles size={48} className="mx-auto mb-2" style={{ color: result.won_item.color }} />
                <p className="text-xl font-bold text-white">{result.won_item.name}</p>
                <p className="text-2xl font-mono font-bold mt-2" style={{ color: result.won_item.color }}>
                  R$ {result.won_item.value}
                </p>
                {result.verification_tag && (
                  <div className="mt-3 pt-3 border-t border-white/20">
                    <p className="text-xs font-mono text-blue-400">{result.verification_tag}</p>
                    {result.random_org_verified && (
                      <p className="text-xs text-green-400 mt-1">✓ Random.org Verified</p>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Case Selection */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        {Object.entries(CASE_CONFIGS).map(([key, config]) => (
          <button
            key={key}
            onClick={() => setSelectedCase(key)}
            className={`p-4 rounded-xl border-2 transition-all ${
              selectedCase === key
                ? `${config.borderColor} bg-gradient-to-br ${config.gradient}/20`
                : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
            }`}
            data-testid={`case-${key}`}
          >
            <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center mx-auto mb-3`}>
              <Gift size={32} className="text-white" />
            </div>
            <p className="font-bold text-white">{config.name}</p>
            <p className="text-sm text-slate-400">R$ {config.price}</p>
          </button>
        ))}
      </div>

      {/* Selected Case Info */}
      <div className="glass rounded-2xl p-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <div className={`w-20 h-20 rounded-xl bg-gradient-to-br ${caseConfig.gradient} flex items-center justify-center`}>
              <Gift size={40} className="text-white" />
            </div>
            <div>
              <h3 className="font-display text-2xl font-bold text-white">{caseConfig.name} Case</h3>
              <p className="text-slate-400">Contains {caseConfig.items.length} possible items</p>
            </div>
          </div>

          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-slate-400 text-sm">Price</p>
              <p className="text-2xl font-bold text-white font-mono">R$ {caseConfig.price}</p>
            </div>
            {user ? (
              <Button
                onClick={handleOpen}
                disabled={loading || opening}
                className="btn-primary text-lg px-8 py-6"
                data-testid="open-case-btn"
              >
                {loading || opening ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Opening...
                  </>
                ) : (
                  <>
                    <Gift className="mr-2 h-5 w-5" />
                    Open Case
                  </>
                )}
              </Button>
            ) : (
              <div className="flex gap-3">
                <a href="/login" className="btn-secondary text-center py-3 px-6 rounded-lg">Sign In</a>
                <a href="/register" className="btn-primary text-center py-3 px-6 rounded-lg">Sign Up</a>
              </div>
            )}
          </div>
        </div>

        {/* Possible Items */}
        <div className="mt-6 pt-6 border-t border-white/10">
          <p className="text-slate-400 text-sm mb-4">Possible Items</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {caseConfig.items.map((item, i) => (
              <div
                key={i}
                className="p-3 rounded-lg text-center"
                style={{ 
                  backgroundColor: `${item.color}10`,
                  border: `1px solid ${item.color}30`
                }}
              >
                <p className="text-sm text-slate-300">{item.name}</p>
                <p className="font-mono font-bold" style={{ color: item.color }}>R$ {item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
